import type { ShippingDestination } from "@loot-os/api-contract";
import { toast } from "@loot-os/ui/components/sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { ErrorAlert } from "../components/ErrorAlert";
import { InheritOrCustomize } from "../components/InheritOrCustomize";
import { ShippingDestinationsEditor } from "../components/ShippingDestinationsEditor";
import { ShippingDestinationsTable } from "../components/ShippingDestinationsTable";
import { StorefrontSection } from "../components/StorefrontSection";
import { useCurrency } from "../lib/currency";
import { useStorefrontConfigQuery } from "../lib/storefront";
import { useTRPC } from "../lib/trpc";
import { useTenantRole } from "../lib/useTenantRole";

/**
 * Where this environment ships and what it costs: a page of its own beside Storefront. It reads
 * the same storefront config the sections do, so it loads that itself, and keys its body on the
 * environment for the reason `StorefrontLayout` keys its outlet: a draft holds one environment's
 * values, and a switch remounts the form rather than carrying the draft across.
 */
export function ShippingPage() {
  const { environmentId, query } = useStorefrontConfigQuery();

  if (query.isLoading) {
    return <div className="text-sm text-muted-foreground">Loading shipping…</div>;
  }
  if (query.error) return <ErrorAlert message={query.error.message} />;
  if (!query.data) return null;

  return <ShippingEnvironment key={environmentId ?? "live"} />;
}

/**
 * One environment's shipping. It inherits the platform default until an editor customizes it; a
 * customized list is saved whole under an optimistic lock, and a save that lands on someone
 * else's is refused (the toast says so) and the page reloads theirs.
 */
function ShippingEnvironment() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const currency = useCurrency();
  const { can } = useTenantRole();
  const { queryOptions, query } = useStorefrontConfigQuery();
  const [customizing, setCustomizing] = useState(false);

  const save = useMutation(
    trpc.storefront.setShippingDestinations.mutationOptions({
      onSuccess: (saved) => {
        setCustomizing(false);
        void queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
        toast.success(
          saved.destinations === null ? "Using the platform default." : "Shipping saved.",
        );
      },
      onError: (err) => {
        // The query client already toasted the message; a stale draft needs the current list.
        if (err.data?.code === "CONFLICT") {
          setCustomizing(false);
          void queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
        }
      },
    }),
  );

  // Narrowing only: the page above renders this once the same query has data.
  if (!query.data) return null;

  const { config, shipping, environment } = query.data;
  const own = config.shippingDestinations;
  const canEdit = can("editor");
  const submit = (destinations: ShippingDestination[] | null) =>
    save.mutate({ destinations, expectedUpdatedAt: shipping.updatedAt });

  return (
    <StorefrontSection
      title="Shipping"
      description="Which countries shoppers can ship to, what each costs them, and how long it takes."
      environment={environment}
    >
      <InheritOrCustomize
        inherited={own === null}
        customizing={customizing}
        canEdit={canEdit}
        saving={save.isPending}
        onCustomize={() => setCustomizing(true)}
        onCancel={() => setCustomizing(false)}
        onRevert={() => submit(null)}
        descriptions={{
          inherited:
            "This environment ships wherever the platform default says. Customize it to set your own countries, costs and estimates; disabled rows are not offered to shoppers.",
          customizing: "Started from the platform default; nothing is saved until you save.",
          customized: "This environment's own list; the platform default no longer applies here.",
        }}
        inheritedSummary={
          <ShippingDestinationsTable destinations={shipping.platformDefault} currency={currency} />
        }
      >
        {(revert) => (
          <ShippingDestinationsEditor
            key={shipping.updatedAt ?? "unsaved"}
            destinations={own ?? shipping.platformDefault}
            currency={currency}
            canEdit={canEdit}
            saving={save.isPending}
            onSave={submit}
            footer={revert}
          />
        )}
      </InheritOrCustomize>
    </StorefrontSection>
  );
}
